import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
} from "react";
import {
  createMercureConnection,
  fetchMercureToken,
  MercureConnection,
} from "./mercureService";

interface MercureContextValue {
  isConnected: boolean;
  error: Error | null;
  reconnect: () => Promise<void>;
}

const MercureContext = createContext<MercureContextValue | null>(null);

interface MercureProviderProps {
  children: React.ReactNode;
  topic: string;
  hubUrl?: string;
  enabled?: boolean;
}

export const MercureProvider: React.FC<MercureProviderProps> = ({
  children,
  topic,
  hubUrl = "https://api.eucalls.net/mercure/.well-known/mercure",
  enabled = true,
}) => {
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const connectionRef = useRef<MercureConnection | null>(null);
  const mountedRef = useRef(true);

  const handleMessage = useCallback((data: any) => {
    // Dispatch custom event for components to listen
    const event = new CustomEvent("mercure:message", { detail: data });
    window.dispatchEvent(event);

    // Also dispatch typed events based on message type
    if (data?.type) {
      const typedEvent = new CustomEvent(`mercure:${data.type}`, {
        detail: data,
      });
      window.dispatchEvent(typedEvent);
    }
  }, []);

  const handleError = useCallback((err: Error) => {
    console.error("Mercure error:", err);
    if (mountedRef.current) {
      setError(err);
      setIsConnected(false);
    }
  }, []);

  const handleConnected = useCallback(() => {
    if (mountedRef.current) {
      setIsConnected(true);
      setError(null);
    }
  }, []);

  const handleDisconnected = useCallback(() => {
    if (mountedRef.current) {
      setIsConnected(false);
    }
  }, []);

  const initialize = useCallback(async () => {
    if (!enabled) return;

    try {
      // Fetch token
      const token = await fetchMercureToken();

      if (!mountedRef.current) return;

      // Create connection
      const connection = createMercureConnection({
        hubUrl,
        topic,
        onMessage: handleMessage,
        onError: handleError,
        onConnected: handleConnected,
        onDisconnected: handleDisconnected,
      });

      connectionRef.current = connection;

      // Connect
      await connection.connect(token);
    } catch (err) {
      console.error("Failed to initialize Mercure:", err);
      if (mountedRef.current) {
        setError(
          err instanceof Error
            ? err
            : new Error("Failed to initialize Mercure"),
        );
      }
    }
  }, [
    enabled,
    hubUrl,
    topic,
    handleMessage,
    handleError,
    handleConnected,
    handleDisconnected,
  ]);

  const reconnect = useCallback(async () => {
    try {
      setError(null);
      const token = await fetchMercureToken();
      await connectionRef.current?.connect(token);
    } catch (err) {
      console.error("Failed to reconnect:", err);
      setError(err instanceof Error ? err : new Error("Failed to reconnect"));
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    initialize();

    return () => {
      mountedRef.current = false;
      connectionRef.current?.disconnect();
      connectionRef.current = null;
    };
  }, [initialize]);

  const value: MercureContextValue = {
    isConnected,
    error,
    reconnect,
  };

  return (
    <MercureContext.Provider value={value}>{children}</MercureContext.Provider>
  );
};

/**
 * Hook to access Mercure connection status
 */
export const useMercure = () => {
  const context = useContext(MercureContext);
  if (!context) {
    throw new Error("useMercure must be used within MercureProvider");
  }
  return context;
};

/**
 * Hook to listen to Mercure messages
 */
export const useMercureMessage = <T = any,>(
  messageType: string | null,
  handler: (data: T) => void,
  deps: React.DependencyList = [],
) => {
  useEffect(() => {
    const eventName = messageType
      ? `mercure:${messageType}`
      : "mercure:message";

    const eventHandler = (event: Event) => {
      const data = (event as CustomEvent).detail;
      handler(data);
    };

    window.addEventListener(eventName, eventHandler);

    return () => {
      window.removeEventListener(eventName, eventHandler);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messageType, ...deps]);
};
